import React from 'react'

import { Stack, Typography, useTheme } from '@mui/material'
import { Handshake, Truck } from 'lucide-react'

import { font, space } from 'settings/tokens'

import { SheetChoiceRow } from './SheetChoiceRow'

/**
 * VERA-632 defines this as `SaleMode` in `@repo/types`
 * (`packages/types/src/transfer/escrow.enum.ts`), which is not on `main` yet. The two string
 * values are copied EXACTLY, so when that branch lands the import comes back and nothing
 * downstream has to change. Do not invent a third value here — the server's escrow windows are
 * keyed on these two.
 */
export const SaleMode = {
  /** Handed over face to face. 48-hour escrow window from payment. */
  InPerson: 'in_person',
  /** Shipped. Tracking required; 7-day window from delivery confirmation. */
  Remote: 'remote',
} as const
export type SaleMode = (typeof SaleMode)[keyof typeof SaleMode]

/**
 * VERA-632 — "Select way": in person or remote.
 *
 * A separate STEP rather than a field on the sell form, matching the designed
 * `Certificate page/Transfer out/Select way` frame. It is also the right shape: this choice
 * is not a detail of the offer, it decides the whole downstream flow — whether a shipping
 * address is collected, whether tracking is required, and how long the buyer's window is.
 *
 * ## The copy states the consequence, not just the option
 *
 * The two modes differ in ways the buyer and seller both feel, and burying that in a
 * help article would mean the seller picks by vibe. Each row says what actually changes:
 * how long the money is held, and what the seller has to do next.
 *
 * The rows themselves are `SheetChoiceRow`, shared with the sell-method chooser — see that file
 * for why they take the action-row treatment rather than the selectable-card one (§34).
 */

interface SaleModeSelectProps {
  onSelect: (mode: SaleMode) => void
}

export const SaleModeSelect = ({ onSelect }: SaleModeSelectProps) => {
  const theme = useTheme()
  return (
    <Stack direction='column' sx={{ gap: `${space.md}px`, paddingBottom: `${space.lg}px` }}>
      <Typography
        sx={{
          fontSize: `${font.label}px`,
          color: 'text.secondary',
          lineHeight: 1.5,
          marginBottom: `${space.xs}px`,
        }}
      >
        How is the item changing hands? This decides how long payment is held.
      </Typography>

      <SheetChoiceRow
        icon={<Handshake size={20} strokeWidth={1.5} color={theme.palette.text.primary} />}
        title='In person'
        description='You hand the item over yourself. Payment is released once the buyer scans the tag, or after 48 hours.'
        onClick={() => onSelect(SaleMode.InPerson)}
      />

      <SheetChoiceRow
        icon={<Truck size={20} strokeWidth={1.5} color={theme.palette.text.primary} />}
        title='Ship it'
        description="You'll add tracking after the sale. Payment is released once the buyer scans the tag, or 7 days after delivery."
        onClick={() => onSelect(SaleMode.Remote)}
      />
    </Stack>
  )
}
